import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MarketplaceEnquiryModal from '@/components/MarketplaceEnquiryModal';
import SEO from '@/components/SEO';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const productTypes = [
  { value: '', label: 'All types' },
  { value: 'physical', label: 'Original / Handmade' },
  { value: 'digital', label: 'Digital assets' },
  { value: 'service', label: 'Creative services' },
];

const formatPrice = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const normalizeProduct = (product = {}) => ({
  ...product,
  id: product.id || product._id || '',
  title: product.title || 'KalaManch product',
  description: product.description || '',
  category: product.category || 'Art',
  subcategory: product.subcategory || '',
  product_type: product.product_type || 'physical',
  price_inr: Number(product.sale_price_inr ?? product.price_inr ?? 0),
  image_urls: Array.isArray(product.image_urls) ? product.image_urls : [],
  artist: product.artist || {},
});

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    let mounted = true;
    const loadProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/marketplace/products`);
        const items = Array.isArray(response.data?.products)
          ? response.data.products
          : [];
        if (mounted) {
          setProducts(items.map(normalizeProduct));
          setError('');
        }
      } catch (err) {
        if (mounted) {
          setError('KalaManch Market could not load right now. Please try again shortly.');
          setProducts([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProducts();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const cleanQuery = query.trim().toLowerCase();
    return products.filter((product) => {
      const typeMatch = type ? product.product_type === type : true;
      if (!cleanQuery) return typeMatch;
      const haystack = [
        product.title,
        product.description,
        product.category,
        product.subcategory,
        product.medium,
        product.artist?.name,
        product.artist?.city,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return typeMatch && haystack.includes(cleanQuery);
    });
  }, [products, query, type]);

  const openOrder = (product) => {
    setSelectedProduct(product);
  };

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28">
      <SEO
        title="KalaManch Market | Buy Indian Art, Crafts & Digital Creative Assets"
        description="KalaManch Market helps buyers discover original Indian paintings, handmade crafts, sculptures, photography, digital artwork, music, voiceovers, and creative assets sold and managed by KalaManch."
        keywords="KalaManch Market, buy Indian art, buy handmade crafts India, Indian artist marketplace, original paintings India, digital artwork India, voiceover assets, artist ecommerce"
      />

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-xl">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
            <div className="bg-gradient-to-br from-[#0F172A] via-[#3B0764] to-[#FF6F00] px-6 py-12 text-white sm:px-10">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-orange-200">
                KalaManch store
              </p>
              <h1 className="mt-4 max-w-2xl text-4xl font-black leading-tight sm:text-6xl">
                KalaManch Market
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-8 text-orange-50">
                A buyer-friendly marketplace for original paintings, sculptures,
                handmade crafts, photography, digital artwork, music, templates,
                voiceovers, and creative assets sold by KalaManch.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white via-orange-50 to-emerald-50 px-6 py-12 sm:px-10">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-[#4A148C]">
                Browse market
              </p>
              <div className="mt-5 grid gap-3 md:grid-cols-[1fr_14rem]">
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base outline-none focus:border-[#FF6F00]"
                  placeholder="Search paintings, crafts, products and services"
                />
                <select
                  value={type}
                  onChange={(event) => setType(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base outline-none focus:border-[#FF6F00]"
                >
                  {productTypes.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <span className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-black text-emerald-700 ring-1 ring-emerald-100">
                  {loading ? 'Loading products...' : `${filteredProducts.length} product(s)`}
                </span>
                <span className="rounded-full bg-orange-50 px-4 py-2 text-sm font-black text-[#C45A00] ring-1 ring-orange-100">
                  Enquire first, confirm later
                </span>
              </div>
              {error && (
                <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filteredProducts.map((product) => {
            const image = product.image_urls?.[0] || '/images/kalamanch-logo.png';
            const artistName = 'Sold by KalaManch';
            return (
              <article
                key={product.id}
                className="flex min-h-[31rem] flex-col overflow-hidden rounded-[1.6rem] border border-slate-200 bg-white shadow-lg"
              >
                <div className="aspect-[4/3] bg-slate-100">
                  <img
                    src={image}
                    alt={product.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#C45A00]">
                      {product.product_type}
                    </span>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                      {product.category}
                    </span>
                  </div>
                  <h2 className="mt-4 line-clamp-2 text-2xl font-black text-slate-950">
                    {product.title}
                  </h2>
                  <p className="mt-2 text-sm font-bold text-[#4A148C]">
                    {artistName}

                  </p>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">
                    {product.description || product.subcategory || 'Curated by KalaManch Market.'}
                  </p>
                  <div className="mt-auto pt-5">
                    <p className="text-2xl font-black text-slate-950">
                      {formatPrice(product.price_inr)}
                    </p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <Link
                        to={`/marketplace/product/${encodeURIComponent(product.id)}`}
                        className="rounded-full border border-[#FF6F00] bg-white px-5 py-3 text-center text-sm font-black text-[#FF6F00] transition hover:-translate-y-0.5"
                      >
                        View details
                      </Link>
                      <button
                        type="button"
                        onClick={() => openOrder(product)}
                        className="rounded-full bg-[#FF6F00] px-5 py-3 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#E65F00]"
                      >
                        Enquire
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {!loading && !filteredProducts.length && (
          <div className="mt-8 rounded-[1.5rem] border border-dashed border-orange-200 bg-white px-5 py-10 text-center">
            <h2 className="text-2xl font-black text-slate-950">No products listed yet.</h2>
            <p className="mt-2 text-slate-600">
              Admin can add products from Admin &gt; Marketplace.
            </p>
          </div>
        )}
      </section>

      {selectedProduct && (
        <MarketplaceEnquiryModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </main>
  );
}
